// =====================
// Imports main.js
// =====================
import { mountImages, lazyMountImages } from "./image.js";
import { images } from "./images.js";
import { initScrollTop } from "./scroll-top.js";

// =====================
// Init
// =====================
init();

function init() {
    initImages();
    initNavigation();
    initSliders();
    initScrollNav();
    initScrollTop();
}

// =====================
// Images
// =====================
function initImages() {
    mountImages(images); // alle normalen Bilder
    lazyMountImages(images); // Slider lazy
}

// =====================
// Navigation (Hamburger Button)
// =====================
function initNavigation() {
    const root = document.documentElement;
    root.classList.add("js");

    const toggleButton = document.querySelector(".menu-toggle");
    const nav = document.getElementById("main-nav");

    if (!toggleButton || !nav) return;

    const firstLink = nav.querySelector("a");

    function openMenu() {
        nav.classList.add("is-open");
        toggleButton.setAttribute("aria-expanded", "true");
        firstLink?.focus();
    }

    function closeMenu() {
        nav.classList.remove("is-open");
        toggleButton.setAttribute("aria-expanded", "false");
        toggleButton.focus();
    }

    toggleButton.addEventListener("click", () => {
        const isOpen = toggleButton.getAttribute("aria-expanded") === "true";
        isOpen ? closeMenu() : openMenu();
    });

    document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") {
            const isOpen = toggleButton.getAttribute("aria-expanded") === "true";
            if (isOpen) closeMenu();
        }
    });
}

// =====================
// Slider
// =====================
function initSliders() {
    document.querySelectorAll(".room").forEach((room) => {
        room.classList.add("js-enabled");

        const track = room.querySelector(".slider-track");
        const slides = room.querySelectorAll(".slide");
        const dots = room.querySelectorAll(".dots button");
        const nextBtn = room.querySelector(".next");
        const prevBtn = room.querySelector(".prev");

        if (!track || slides.length === 0) return;

        let currentIndex = 0;

        const update = (index) => {
            const width = track.offsetWidth;
            if (!width) return;

            track.scrollTo({
                left: index * width,
                behavior: "smooth",
            });

            dots.forEach((d) => d.classList.remove("active"));
            dots[index]?.classList.add("active");
        };

        // 👉 Buttons
        nextBtn?.addEventListener("click", () => {
            currentIndex = (currentIndex + 1) % slides.length;
            update(currentIndex);
        });

        prevBtn?.addEventListener("click", () => {
            currentIndex = (currentIndex - 1 + slides.length) % slides.length;
            update(currentIndex);
        });

        // 👉 Dots
        dots.forEach((dot, i) => {
            dot.addEventListener("click", () => {
                currentIndex = i;
                update(currentIndex);
            });
        });

        // Set initial dot state
        dots[0]?.classList.add("active");

        // 👉 Scroll Sync (Swipe)
        track.addEventListener(
            "scroll",
            () => {
                const index = Math.round(track.scrollLeft / track.offsetWidth);

                if (index !== currentIndex) {
                    currentIndex = index;

                    dots.forEach((d) => {
                        d.classList.remove("active");
                        d.removeAttribute("aria-current");
                    });

                    dots[index]?.classList.add("active");
                    dots[index]?.setAttribute("aria-current", "true");
                }
            },
            { passive: true },
        );

        // 👉 Resize Fix
        window.addEventListener("resize", () => {
            track.scrollTo({
                left: currentIndex * track.offsetWidth,
            });
        });
    });
}

// =====================
// Scroll Main Navigation (Hide/Show)
// =====================
function initScrollNav() {
    const nav = document.getElementById("main-nav");
    if (!nav) return;

    const mediaQuery = window.matchMedia("(max-width: 768px)");

    let prevScrollY = window.scrollY;
    let ticking = false;

    const threshold = 10;

    const showNav = () => {
        nav.classList.remove("nav-hidden");
    };

    const hideNav = () => {
        // Mobile-Menü nie verstecken
        if (nav.classList.contains("is-open")) return;

        nav.classList.add("nav-hidden");
    };

    const handleScroll = () => {
        if (mediaQuery.matches) {
            ticking = false;
            return;
        }

        const currentScrollY = Math.max(window.scrollY, 0);
        const diff = currentScrollY - prevScrollY;

        if (Math.abs(diff) < threshold) {
            ticking = false;
            return;
        }

        if (currentScrollY <= 0) {
            showNav();
        } else if (diff > 0) {
            hideNav();
        } else {
            showNav();
        }

        prevScrollY = currentScrollY;
        ticking = false;
    };

    const onScroll = () => {
        if (!ticking) {
            window.requestAnimationFrame(handleScroll);
            ticking = true;
        }
    };

    const handleMediaChange = (e) => {
        nav.classList.remove("nav-hidden");

        if (e.matches) {
            window.removeEventListener("scroll", onScroll);
        } else {
            prevScrollY = window.scrollY;
            window.addEventListener("scroll", onScroll, {
                passive: true,
            });
        }
    };

    handleMediaChange(mediaQuery);
    mediaQuery.addEventListener("change", handleMediaChange);
}

// Service Worker Registrierung für PWA (Progressive Web App)
if ("serviceWorker" in navigator) {
    window.addEventListener("load", () => {
        import("virtual:pwa-register").then(({ registerSW }) => {
            registerSW({ immediate: false });
        });
    });
}
